import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import AdminSidebar from "./SideBar";
import AdminHeader from "./Header";
import QuillEditor from "../../components/Content"; // Import the QuillEditor component
import defaultImage from "../../assets/defaultImagecompressed.jpg"; // Import the default image
import './style.css';

const UpdateArticle = () => {
  const { ArticleID } = useParams(); // Get articleID from URL instead of slug
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [authors, setAuthors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [articleData, setArticleData] = useState({
    title: "",
    content: "",
    publishedDate: "",
    authorID: "",
    categoryID: "",
    imageUrl: "", // Add imageUrl field to state
    useDefaultImage: true, // Track if default image is used
    articleID: "" // Store articleID in state
  });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setIsSidebarOpen(true);

  const fetchData = async (type, setData) => {
    try {
      const response = await fetch(`https://homchang.in/get_data.php?type=${type}`);
      const result = await response.json();
      console.log(result.data);

      if (result.success) {
        setData(result.data);
      } else {
        console.error(`Error fetching ${type}:`, result.message);
      }
    } catch (error) {
      console.error(`Error fetching ${type}:`, error);
    }
  };

  useEffect(() => {
    fetchData("authors", setAuthors);
    fetchData("categories", setCategories);

    // Fetch article data based on articleID
    if (ArticleID) {
      fetchArticle(ArticleID);
    }
  }, [ArticleID]);

  useEffect(() => {
    console.log("Authors:", authors);
    console.log("Categories:", categories);
  }, [authors, categories]);

  const fetchArticle = async (id) => {
    try {
      // Make sure parameter name matches exactly what backend expects
      const response = await fetch(`https://homchang.in/get_article.php?ArticleId=${id}`);
      const result = await response.json();
      console.log(result.data);

      if (result.success) {
        // Make sure field names match between your backend response and frontend state
        setArticleData({
          title: result.data.Title,
          content: result.data.Content,
          publishedDate: result.data.PublishedDate,
          authorID: result.data.AuthorId, // Make sure these match the response structure
          categoryID: result.data.CategoryId,
          imageUrl: result.data.ImageURL || defaultImage,
          useDefaultImage: !result.data.ImageURL,
          articleID: id
        });
      } else {
        console.error("Error fetching article:", result.message);
        setMessage({
          text: "Error loading article: " + result.message,
          type: "error"
        });
      }
    } catch (error) {
      console.error("Fetch Error:", error);
      setMessage({
        text: "An error occurred while loading the article.",
        type: "error",
      });
    }
  };

  // Cloudinary upload handler
  const handleUpload = () => {
    setIsUploading(true);
    window.cloudinary.openUploadWidget(
      {
        cloudName: process.env.REACT_APP_CLOUDINARY_CLOUD_NAME,
        uploadPreset: "Article_images",
        sources: ["local", "url"],
        multiple: false,
        folder: "articles",
        clientAllowedFormats: ["jpg", "png", "jpeg"],
        maxImageFileSize: 2000000,
      },
      (error, result) => {
        setIsUploading(false);
        if (!error && result && result.event === "success") {
          setArticleData((prev) => ({
            ...prev,
            imageUrl: result.info.secure_url,
            useDefaultImage: false,
          }));
        }
      }
    );
  };

  const handleDefaultImageChange = (e) => {
    const useDefault = e.target.checked;
    setArticleData({
      ...articleData,
      useDefaultImage: useDefault,
      imageUrl: useDefault ? defaultImage : articleData.imageUrl // Set default image URL if using default
    });
  };

  const handleRemoveImage = () => {
    setArticleData((prev) => ({
      ...prev,
      imageUrl: defaultImage, // Reset to default image URL
      useDefaultImage: true // Check default image option
    }));
  };

  const handleArticleUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ text: "", type: "" });

    if (!ArticleID) {
        setMessage({ text: "Article ID is missing in the URL!", type: "error" });
        setIsLoading(false);
        return;
    }

    const formData = new URLSearchParams();
    formData.append("title", articleData.title);
    formData.append("content", articleData.content);
    formData.append("publishedDate", articleData.publishedDate);
    formData.append("authorID", articleData.authorID);
    formData.append("categoryID", articleData.categoryID);
    formData.append("useDefaultImage", articleData.useDefaultImage); // Send default image option

    if (articleData.imageUrl && !articleData.useDefaultImage) {
        formData.append("imageURL", articleData.imageUrl);
    }

    try {
        const response = await fetch(`https://homchang.in/update_articles.php?ArticleId=${ArticleID}`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: formData
        });

        const result = await response.json();
        setIsLoading(false);

        if (result.success) {
            setMessage({ text: "Article updated successfully!", type: "success" });
            fetchArticle(ArticleID); // Refresh article data
        } else {
            console.error("Server Error:", result.message);
            setMessage({ text: result.message, type: "error" });
        }
    } catch (error) {
        console.error("Fetch Error:", error);
        setIsLoading(false);
        setMessage({ text: "An error occurred while updating the article.", type: "error" });
    }
};

  return (
    <div className="flex h-screen bg-gray-100">
      <AdminSidebar isOpen={isSidebarOpen} closeSidebar={closeSidebar} />
      <div className={`flex-1 transition-all duration-300 ${isSidebarOpen ? "ml-64" : "ml-0"}`}>
        <AdminHeader toggleSidebar={toggleSidebar} title="Update Article" />
        <div className="pt-20 px-8">
          <h2 className="text-4xl font-bold mb-6 text-gray-800">Update Article</h2>

          {message.text && (
            <div className={`p-4 rounded-md mb-6 ${message.type === "success" ? "bg-green-100 text-green-700" : message.type === "info" ? "bg-blue-100 text-blue-700" : "bg-red-100 text-red-700"}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleArticleUpdate} className="space-y-6">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Title</label>
              <input
                type="text"
                placeholder="Enter article title"
                value={articleData.title}
                onChange={(e) =>
                  setArticleData({ ...articleData, title: e.target.value })
                }
                required
                className="w-full border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">Content</label>
              <QuillEditor
                value={articleData.content}
                onChange={(content) =>
                  setArticleData({ ...articleData, content })
                }
              />
            </div>

            {/* Image Upload Section */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
               Upload Main Article Image
              </label>
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={handleUpload}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  {isUploading ? 'Uploading...' : 'Upload Image'}
                </button>
                {articleData.imageUrl && !articleData.useDefaultImage && (
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                  >
                    Remove Image
                  </button>
                )}
              </div>
              {isUploading && (
               <div className="loading-spinner">
                 
                  Loading...
               </div>
           )}
              {articleData.imageUrl && (
                <div className="mt-4">
                  <img
                    src={articleData.imageUrl}
                    alt="Article Preview"
                    className="w-64 h-auto rounded-md"
                  />
                </div>
              )}
            </div>

            {/* Default Image Option */}
            <div>
              <label className="block text-gray-700 font-medium mb-2">
                <input
                  type="checkbox"
                  checked={articleData.useDefaultImage}
                  onChange={handleDefaultImageChange}
                  className="mr-2"
                />
                No Main Image
              </label>
              {!articleData.useDefaultImage && (
                <input
                  type="text"
                  value={articleData.imageUrl}
                  onChange={(e) =>
                    setArticleData({
                      ...articleData,
                      imageUrl: e.target.value
                    })
                  }
                  placeholder="Enter custom image URL"
                  className="mt-2 p-2 border rounded"
                />
              )}
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="flex-1">
                <label className="block text-gray-700 font-medium mb-2">Published Date</label>
                <input
                  type="date"
                  value={articleData.publishedDate}
                  onChange={(e) =>
                    setArticleData({
                      ...articleData,
                      publishedDate: e.target.value,
                    })
                  }
                  required
                  className="w-full border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200 focus:outline-none"
                />
              </div>
              <div className="flex-1">
                <label className="block text-gray-700 font-medium mb-2">Author</label>
                <select
                  value={articleData.authorID}
                  onChange={(e) =>
                    setArticleData({ ...articleData, authorID: e.target.value })
                  }
                  required
                  className="w-full border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200 focus:outline-none"
                >
                  <option value="">Select an author</option>
                  {authors.map((author) => (
                    <option key={author.AuthorID} value={author.AuthorID}>
                      {author.Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-gray-700 font-medium mb-2">Category</label>
                <select
                  value={articleData.categoryID}
                  onChange={(e) =>
                    setArticleData({
                      ...articleData,
                      categoryID: e.target.value,
                    })
                  }
                  required
                  className="w-full border-gray-300 rounded-lg p-3 focus:ring focus:ring-blue-200 focus:outline-none"
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category.CategoryID} value={category.CategoryID}>
                      {category.CategoryName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 rounded-lg text-white ${isLoading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
            >
              {isLoading ? "Updating..." : "Update Article"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UpdateArticle;
